import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Lock, Globe } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { cn } from '@epoch-ui/lib/utils';
import { DateTimePickerField } from '@epoch-ui/components/Form/fields/DateTimePickerField/DateTimePickerField';
import { Badge } from '@epoch-ui/components/Badge';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { SLACK_REVAMP } from 'constants/featureFlags';
import { DebouncedInputField } from '@epoch-ui/components/Form/fields/DebouncedInputField';
import { SwitchField } from '@epoch-ui/components/Form/fields/SwitchField';
import { companySlackApi } from 'api';
import { getGMTOffset } from 'utils/dateTimeUtils';
import { formatTimezoneDisplay } from 'utils/format';

import { useEditableEvent } from '../../../hooks/useEditableEvent';

import { SlackSettingsFormValues, convertSlackChannelDateToFormValue } from './slackSettingsSchema';
import { SlackWorkspaceSelector } from './SlackWorkspaceSelector';

interface CreateNewChannelComponentProps {
  disabled?: boolean;
}

export const CreateNewChannelComponent: React.FC<CreateNewChannelComponentProps> = ({
  disabled = false
}) => {
  const { formState, setError, clearErrors, watch } = useFormContext<SlackSettingsFormValues>();
  const { errors } = formState;
  const isSlackRevampEnabled = useFeatureFlag(SLACK_REVAMP);
  const event = useEditableEvent();

  const channelName = watch('slackChannelName');

  const { refetch: checkChannel } = useQuery({
    queryKey: ['dashboard', 'editEventSettings', 'checkSlackChannelExists', channelName],
    queryFn: async () => {
      const trimmedChannelName = channelName?.trim() || '';
      const channelExists = await companySlackApi.channelNameExists(trimmedChannelName);

      if (channelExists) {
        // If the channel exists, set an error
        setError('slackChannelName', {
          type: 'duplicate',
          message: 'That channel already exists.'
        });
      }

      return channelExists;
    },
    enabled: false, // Don't run automatically
    retry: false
  });

  const handleChannelNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    const trimmedValue = value.trim();

    // Clear any existing duplicate error when user starts typing
    if (errors.slackChannelName?.type === 'duplicate') {
      clearErrors('slackChannelName');
    }
    if (trimmedValue) {
      checkChannel();
    }
  };

  const calendarDisabled = useMemo(() => {
    const disabledDates: any = {};

    // Disable dates in the past
    disabledDates.before = convertSlackChannelDateToFormValue(
      new Date().toISOString(),
      event.timezoneCode
    );

    // Disable dates after event start time
    // Convert UTC datetime from DB to form value (Date object in browser timezone)
    if (event.datetimeScheduled) {
      disabledDates.after = convertSlackChannelDateToFormValue(
        event.datetimeScheduled,
        event.timezoneCode
      );
    }

    return disabledDates;
  }, [event.datetimeScheduled, event.timezoneCode]);

  return (
    <div className="tw-w-full">
      {isSlackRevampEnabled && <SlackWorkspaceSelector disabled={disabled} />}
      <div className="tw-relative tw-mb-4">
        <DebouncedInputField
          name="slackChannelName"
          label="Channel Name"
          inputClassName="tw-pr-24 tw-pl-7"
          className={'tw-mt-0'}
          debounce={500}
          onChange={handleChannelNameChange}
          disabled={disabled}
        />
        <Badge
          className={cn(
            'tw-absolute tw-right-4 tw-top-9 tw-flex !tw-items-center !tw-justify-center tw-gap-1 tw-rounded-lg !tw-px-[6px] !tw-py-[3px] tw-text-xs tw-font-medium',
            disabled && 'tw-opacity-40'
          )}
          variant="secondary"
        >
          {event.isPrivate ? (
            <Lock strokeWidth={3} className="tw-mr-[2px] tw-h-3 tw-w-3" />
          ) : (
            <Globe className="tw-mr-[2px] tw-h-3 tw-w-3" />
          )}
          <span className="tw-text-xs tw-font-medium tw-leading-4">
            {event.isPrivate ? 'Private' : 'Public'}
          </span>
        </Badge>
        <span
          className={cn(
            'tw-absolute tw-left-4 tw-top-[38px] tw-leading-5 tw-text-medium-grey',
            disabled && 'tw-opacity-40'
          )}
        >
          #
        </span>
        {!errors.slackChannelName && (
          <div className="tw-mb-[7px] tw-mt-2">
            <span className="tw-text-xs tw-font-normal tw-text-primary-grey">
              Only lowercase letters, numbers, hyphens, underscores
            </span>
          </div>
        )}
      </div>

      <div className="tw-mb-4">
        <DateTimePickerField name="slackChannelDate" label="Creation Date" disabled={disabled}>
          <DateTimePickerField.DateInput
            placeholder="Date"
            className="tw-w-[170px]"
            disabled={calendarDisabled}
          />
          <DateTimePickerField.Time placeholder="Time" className="tw-w-[170px]" interval={60} />
        </DateTimePickerField>
        <div className="tw-mt-2 tw-text-xs tw-leading-4 tw-text-primary-grey">
          You&apos;re scheduling in ({getGMTOffset(event.timezoneCode || 'UTC')}) -{' '}
          {formatTimezoneDisplay(event.timezoneCode || 'UTC')}.
        </div>
      </div>

      <div>
        <SwitchField
          name="slackChannelAutoarchive"
          label="Archive channel after event ends? "
          description="This can take up to 48 hours"
          className="tw-mb-4 tw-flex-row-reverse tw-justify-between"
        />
      </div>
    </div>
  );
};
